import { Component, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BRL_MASK_OPTIONS } from 'app/helpers/money';
import { Product, ProductCategory } from 'app/entities';
import { Unit } from 'app/entities/unit';
import { ProductCategoriesService } from 'app/main/pages/products/product-category.service';
import { ProductsService } from 'app/main/pages/products/product.service';
import { UnitService } from 'app/main/pages/products/unit.service';
import { AlertService } from 'app/services/alert.service';
import { GtagService } from 'app/services/gtag.service';
import { SingletonsService } from 'app/services/singletons.service';
import { StringFilterByPipe } from 'app/string-filter-by.pipe';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from "rxjs/operators";
@Component({
  selector: 'app-form-product-create-or-update',
  templateUrl: './form-product-create-or-update.component.html',
  styleUrls: ['./form-product-create-or-update.component.scss']
})
export class FormProductCreateOrUpdateComponent implements OnInit {
  /** Dentro do diálogo de cadastro: as ações ficam no rodapé do diálogo */
  @Input() inDialog = false
  readonly brlMask = BRL_MASK_OPTIONS;
  @Input('product') product: Product = new Product();
  @Input('showImageInput') showImageInput: boolean = true;
  @Input('showStatusInput') showStatusInput: boolean = true;
  @ViewChild('nomeInput') nomeInput?: any;
  @Output() onCreatedProduct = new Subject<Product>();
  @Output() onUpdatedProduct = new Subject<Product>();

  public bankFilterCtrl: FormControl = new FormControl();
  public unitFilterCtrl: FormControl = new FormControl();
  productCategoriesFiltered: ReplaySubject<ProductCategory[]> = new ReplaySubject<ProductCategory[]>(1);
  unitsFiltered: ReplaySubject<Unit[]> = new ReplaySubject<Unit[]>(1);
  loading = false;
  form?: FormGroup
  productCategories?: ProductCategory[] = []
  units: Unit[] | undefined = [];
  itemCreated: boolean = false
  private _onDestroy = new Subject<void>();

  constructor(
    private _formBuilder: FormBuilder,
    private _alertService: AlertService,
    private _productService: ProductsService,
    private _productCategoryService: ProductCategoriesService,
    private _uniitService: UnitService,
    private _singletonService: SingletonsService,
    private stringFilterByPipe: StringFilterByPipe,
    private _gtagService: GtagService
  ) {
    this.loadProductCategories()
    this.loadUnits()
  }

  ngOnInit(): void {
    if (!this.product?.id) this.product.status = 1
    if (this.product?.stockControlEnabled === undefined || this.product?.stockControlEnabled === null) {
      this.product.stockControlEnabled = true
    }

    this.createForm();

    // listen for search field value changes
    this.bankFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterProductCategories();
      });

    this.unitFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterUnits();
      })
  }

  createForm() {
    this.form = this._formBuilder.group({
      id: [this.product?.id, []],
      product_category_id: [this.product?.productCategoryId, []],
      title: [this.product?.title, [Validators.required, Validators.maxLength(400)]],
      code: [this.product?.code, []],
      image: [this.product?.image, []],
      description: [this.product?.description, [Validators.maxLength(1000)]],
      unit_id: [this.product?.unitId, [Validators.required]],
      valor: [this.product?.valor, [Validators.required]],
      valor_compra: [this.product?.valorCompra, []],
      estoque_minimo: [this.product?.estoqueMinimo, []],
      stock_control_enabled: [this.product?.stockControlEnabled ?? true, []],
      status: [this.product?.status ?? 1, []],
    });

    // Só no cadastro: na edição (detalhe do produto) o foco abria o teclado do celular
    if (!this.product?.id) {
      setTimeout(() => this.nomeInput?.nativeElement.focus(), 100);
    }
  }

  store() {
    if (this.form?.valid) {
      this.loading = true
      this._productService.store(this.form?.value).subscribe((res) => {
        this._alertService.success('Produto criado');
        this._gtagService.event('product_created', {
          event_category: 'engagemnt',
          event_label: 'Produto criado'
        });
        this.onCreatedProduct.next(res)
        this.itemCreated = true
        this.loading = false
        this.form?.reset()
        this.product.stockControlEnabled = true
        this.form?.patchValue({ stock_control_enabled: true, status: 1 })
        
        // Resetar flag após componente processar
        setTimeout(() => {
          this.itemCreated = false
        }, 100);
      }, (error) => {
        this._alertService.error(error)
        this.loading = false
      })
    }
  }

  update() {
    if (this.form?.valid && this.product && this.product.id) {
      this.loading = true
      this._productService.update(this.product.id, this.form?.value).subscribe((res) => {
        this._alertService.success('Produto atualizado');
        this.onUpdatedProduct.next(res);
        this.form?.markAsPristine()
        this.loading = false

        this._gtagService.event('product_updated', {
          event_category: 'engagemnt',
          event_label: 'Produto atualizado'
        });
      }, (error) => {
        this.loading = false
        this._alertService.error(error)
      })
    }
  }

  loadProductCategories() {
    this._productCategoryService.simpleProductCategoriesAll().subscribe((response) => {
      this.productCategories = response.data
      this.productCategoriesFiltered.next(this.productCategories?.slice());
    })
  }

  loadUnits() {
    this._uniitService.simpleUnitsAll().subscribe((response) => {
      this.units = response?.data;
      this.unitsFiltered.next(this.units?.slice());
    });
  }

  onImageChanged(event: File | null) {
    // Edição: a imagem é salva na hora (upload/remoção) e o campo do formulário passa a ter
    // a URL salva, sem marcar alteração pendente. Antes o campo ficava com o arquivo e
    // "Salvar alterações" o reenviava no `image`; e a remoção só avisava, sem salvar.
    if (this.product?.id) {
      if (event) {
        this._productService.changePhoto(this.product.id, event).subscribe((res: any) => {
          const image = res?.data?.image ?? res?.image ?? null;
          this.product.image = image ?? this.product.image
          this.form?.get('image')?.setValue(this.product.image, { emitEvent: false })
          this._alertService.success('Imagem atualizada com sucesso.')
        }, (error) => {
          this._alertService.error(error)
        });
      } else {
        this._productService.deletePhoto(this.product.id).subscribe(() => {
          this.product.image = undefined
          this.form?.get('image')?.setValue(null, { emitEvent: false })
          this._alertService.success('Imagem removida.')
        }, (error) => {
          this._alertService.error(error)
        });
      }
      return;
    }

    // Cadastro: a imagem vai junto ao criar o produto
    this.form?.get('image')?.setValue(event || null)
    this.form?.markAsDirty()
  }

  productCategoryCreated(productCategory, productCategorySelect) {
    if (productCategory) {
      productCategorySelect.close();
      this.bankFilterCtrl.reset();
      this.productCategories?.push(productCategory);
      this.productCategoriesFiltered.next(this.productCategories?.slice());
      this.form?.controls.product_category_id.setValue(productCategory.id);

      this._gtagService.event('product_category_created', {
        event_category: 'engagemnt',
        event_label: 'Categoria de produto criada'
      });
    }
  }

  unitCreated(unit, unitSelect) {
    if (unit) {
      unitSelect.close();
      this.unitFilterCtrl.reset();
      this.units?.push(unit);
      this.unitsFiltered.next(this.units?.slice());
      this.form?.controls.unit_id.setValue(unit.id);

      this._gtagService.event('product_unit_created', {
        event_category: 'engagemnt',
        event_label: 'Unidade de produto criada'
      });
    }
  }

  filterProductCategories() {
    if (!this.productCategories) {
      return;
    }
    // get the search keyword
    let search = this.bankFilterCtrl.value;
    if (!search) {
      this.productCategoriesFiltered.next(this.productCategories.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.productCategoriesFiltered.next(
      this.stringFilterByPipe.transform(this.productCategories, search, "title")
    );
  }

  filterUnits() {
    if (!this.units) {
      return;
    }
    // busca do select de unidade (antes lia a busca da categoria e nunca filtrava)
    let search = this.unitFilterCtrl.value;
    if (!search) {
      this.unitsFiltered.next(this.units.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.unitsFiltered.next(
      this.stringFilterByPipe.transform(this.units, search, "name")
    );
  }

  toggleStatus(product: Product, active: boolean) {
    product.status = active ? 1 : 2
    this.form?.controls.status.setValue(product.status)
    this.form?.markAsDirty()
  }

  toggleStockControl(active: boolean) {
    this.product.stockControlEnabled = active
    this.form?.controls.stock_control_enabled.setValue(active)
    this.form?.markAsDirty()
  }

}
